import types from "../types";
import {
  RED_COLOR,
  FIELD_PLACEHOLDER_COLOR_DARK,
  GREY_COLOR,
  GREY_DARK_COLOR,
  GRADIENT_PURPLE_FUCHSIA,
  PAPER_BACKGROUND_COLOR,
  PAPER_CONTRAST_TEXT,
  PAPER_SECONDARY_CONTRAST_TEXT,
  PAPER_SECONDARY_BACKGROUND_COLOR,
  PRIMARY_COLOR_TEXT,
  SECONDARY_COLOR,
  PAPER_BACKGROUND_COLOR_SLIDE_MENU,
  FIELD_BACKGROUND_COLOR,
  GRADIENT_BLACK_COLOR,
  GRADIENT_BLACK_COLOR_TEXT,
  GRADIENT_BLACK_COLOR_HOVER,
  DISABLED_COLOR_DARK,
  SECONDARY_COLOR_TEXT,
  PRIMARY_COLOR,
} from "../utils/colors";
import {
  BORDER_RADIUS,
  BORDER_RADIUS_BUTTON,
  DEFAULT_NAME_BUSINESS,
  FONT_FAMILY,
} from "../utils/constants";
import {
  APP_DROPDOWN_Z_INDEX,
  APP_DROPDOWN_Z_INDEX_CSS,
} from "../utils/modalLayer";
import { getSlideMenuCollapsedFromStorage } from "../utils/slideMenuCollapsedStorage";
import { inputPlaceholderSx } from "../styles";

const themeCommon = {
  typography: {
    fontFamily: FONT_FAMILY,
  },
  zIndex: {
    modal: 1300,
    snackbar: APP_DROPDOWN_Z_INDEX,
  },
  components: {
    MuiLoadingButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        loadingIndicator: {
          color: "rgba(255, 255, 255, 0.54)",
        },
        root: (props) => {
          return {
            "&.Mui-disabled": {
              filter:
                props?.sx?.background === GRADIENT_PURPLE_FUCHSIA
                  ? "brightness(0.4)"
                  : "brightness(1)",
            },
          };
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          "&:not(.MuiButton-root)": {
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          zIndex: 1300,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          zIndex: APP_DROPDOWN_Z_INDEX_CSS,
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          "&.MuiAutocomplete-popper": {
            zIndex: APP_DROPDOWN_Z_INDEX_CSS,
          },
        },
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        root: {
          zIndex: APP_DROPDOWN_Z_INDEX_CSS,
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => {
          return {
            "&.Mui-checked": {
              color: `${theme.palette.text.primary}`,
            },
            "&.MuiCheckbox-indeterminate": {
              color: `${theme.palette.text.primary}`,
            },
            "&.Mui-disabled": {
              color: "rgba(0, 0, 0, 0.3)",
            },
          };
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        input: ({ theme }) => ({
          ...inputPlaceholderSx(theme.palette.fields.placeholder),
          "&.Mui-disabled": {
            opacity: 1,
            WebkitTextFillColor: "rgba(0, 0, 0, 0.5)",
          },
        }),
      },
    },

    MuiClockNumber: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: "#fff !important",
            color: "#000 !important",
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent !important",
          color: "#000 !important",
        },
      },
    },
    MuiDayPickerWeekDayLabel: {
      styleOverrides: {
        root: {
          color: "#fff !important",
        },
      },
    },
    MuiYearPicker: {
      styleOverrides: {
        root: {
          color: "#000 !important",
          backgroundColor: "transparent !important",
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff !important",
          color: "#000 !important",
          "&.Mui-selected": {
            backgroundColor: "#000 !important",
            color: "#fff !important",
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: `${PRIMARY_COLOR_TEXT} !important`,
          ".MuiButton-root &": {
            color: "inherit !important",
          },
          ".Mui-disabled &": {
            color: `${DISABLED_COLOR_DARK} !important`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: `${PRIMARY_COLOR_TEXT} !important`,
        },
      },
    },

    MuiClock: {
      styleOverrides: {
        pin: {
          backgroundColor: "#fff",
        },

        amButton: (props) => {
          return {
            backgroundColor:
              props?.ownerState?.meridiemMode === "am" ? "black" : "white",
            color: props?.ownerState?.meridiemMode === "am" ? "white" : "black",
          };
        },

        pmButton: (props) => {
          return {
            backgroundColor:
              props?.ownerState?.meridiemMode === "pm" ? "black" : "white",
            color: props?.ownerState?.meridiemMode === "pm" ? "white" : "black",
          };
        },
      },
    },
    MuiClockPointer: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
        },
        thumb: {
          backgroundColor: "transparent",
          border: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: `${BORDER_RADIUS_BUTTON * 4}px !important`,
          paddingLeft: "18px !important",
          paddingRight: "18px !important",
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        componentsProps: {
          popper: {
            sx: { zIndex: APP_DROPDOWN_Z_INDEX_CSS },
          },
        },
      },
      styleOverrides: {
        root: (props) => {
          const hasRenderTags = props?.ownerState?.renderTags;
          const hasLabel = props?.ownerState?.hasLabel;

          if (hasLabel) {
            return {
              "& .MuiFilledInput-root": {
                paddingTop: `${3 * 8}px !important`,
                paddingBottom: `${1 * 8}px !important`,
              },
            };
          }

          if (!hasRenderTags) {
            return {
              "& .MuiFilledInput-root": {
                paddingTop: `${3 * 8}px !important`,
                paddingBottom: `${1 * 8}px !important`,
              },
              "& .MuiInputBase-input": {
                paddingTop: "16px !important",
                paddingBottom: "17px !important",
              },
              "& .MuiInputBase-root": {
                paddingTop: "0px !important",
                paddingBottom: "0px !important",
              },
            };
          }

          return {
            "& .MuiFilledInput-root": {
              paddingTop: `${3 * 8}px !important`,
              paddingBottom: `${1 * 8}px !important`,
            },
            "& .MuiInputBase-input": {
              paddingTop: "0px !important",
              paddingBottom: "0px !important",
            },
            "& .MuiInputBase-root": {
              paddingTop: "8px !important",
              paddingBottom: "6px !important",
              minHeight: "unset",
            },
          };
        },
        listbox: {
          "& .MuiAutocomplete-option": {
            backgroundColor: "transparent !important",
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
          "& .MuiAutocomplete-option.Mui-focused": {
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
          "& .MuiAutocomplete-option[aria-selected='true']": {
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&:not(.Mui-focused):not(.MuiInputLabel-shrink)": {
            marginTop: 6,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: PRIMARY_COLOR_TEXT,
        },
        root: {
          ".MuiFilledInput-input:focus": {
            backgroundColor: "unset",
          },
          "&.Mui-disabled": {
            WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
        },
        select: ({ theme }) => ({
          color: `${PRIMARY_COLOR_TEXT} !important`,
          WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
          ...inputPlaceholderSx(theme.palette.fields.placeholder),
          "&.Mui-focused": {
            color: `${PRIMARY_COLOR_TEXT} !important`,
            WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
          },
          "&.Mui-disabled": {
            opacity: 1,
            WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            WebkitTextFillColor: "#000 !important",
            opacity: 0.68,
            pointerEvents: "none",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: PRIMARY_COLOR_TEXT,
          borderRadius: `${BORDER_RADIUS * 4}px !important`,
          "&.MuiFilledInput-root": {
            "&.Mui-disabled": {
              WebkitTextFillColor: "transparent !important",
            },
            ":hover": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            ":before": {
              borderBottom: "none !important",
            },
            ":after": {
              borderBottom: "none !important",
            },
          },
        },
        inputMultiline: {
          // overflow: "hidden !important",
          paddingRight: "12px !important",
        },
        input: ({ theme }) => ({
          color: `${PRIMARY_COLOR_TEXT} !important`,
          WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
          ".MuiBox-root": {
            minHeight: "37px !important",
            overflow: "hidden !important",
          },
          "&.MuiFilledInput-input:not(.MuiInputBase-inputMultiline):not(.MuiAutocomplete-input), &.MuiFilledInput-input.MuiAutocomplete-input.MuiInputBase-inputHiddenLabel":
            {
              minHeight: "37px",
            },
          ...inputPlaceholderSx(theme.palette.fields.placeholder),
          "&:-webkit-autofill": {
            boxShadow: `0 0 0 100px transparent inset !important`,
            WebkitTextFillColor: `${PRIMARY_COLOR_TEXT} !important`,
          },
        }),
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          borderRadius: `${BORDER_RADIUS * 4}px !important`,
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          ".MuiFormControlLabel-label.Mui-disabled": {
            color: "#666666 !important",
            opacity: 0.9,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            color: `${theme.palette.background.paperContrastText}`,
            "&.Mui-disabled": {
              WebkitTextFillColor: "#000 !important",
              color: "#000 !important",
            },
          };
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            "&.Mui-checked": {
              color: theme.palette.text.primary,
            },
          };
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: ({ theme }) => {
          return {
            color: theme.palette.text.primary,
          };
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: () => {
          return {
            backgroundColor: "rgba(0,0,0,0.2)",
          };
        },
        bar: () => {
          return {
            backgroundColor: "black",
          };
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: `${PRIMARY_COLOR_TEXT} !important`,
          "&.Mui-selected": {
            backgroundColor: "transparent !important",
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
          "&.Mui-selected:hover": {
            backgroundColor: "transparent !important",
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
          "&:hover": {
            backgroundColor: "transparent !important",
            color: `${PRIMARY_COLOR_TEXT} !important`,
          },
        },
      },
    },
  },
  favicon:
    "https://clapp-prod.s3.us-east-1.amazonaws.com/Buzzly/favicon-white.ico?v=2",
  nameBusiness: DEFAULT_NAME_BUSINESS,
  logoInvoice: "https://clapp-prod.s3.amazonaws.com/Buzzly/logo.png?v=2",
};

const themeDark = {
  ...themeCommon,
  logo: "https://clapp-prod.s3.us-east-1.amazonaws.com/Buzzly/logo.png",
  logoWhite: "https://clapp-prod.s3.amazonaws.com/Buzzly/logo-white.png?v=2",
  logoChat: "https://clapp-prod.s3.amazonaws.com/Buzzly/favicon.ico?v=2",
  logoLogin: "https://clapp-prod.s3.us-east-1.amazonaws.com/Buzzly/logo.png",
  logoLoginWhite:
    "https://clapp-prod.s3.amazonaws.com/Buzzly/logo-white.png?v=2",
  logoNavbar: "https://clapp-prod.s3.amazonaws.com/Buzzly/logo-white.png?v=2",
  logoError: "https://clapp-prod.s3.amazonaws.com/Buzzly/logo-white.png?v=2",
  palette: {
    mode: "dark",
    text: {
      primary: PRIMARY_COLOR_TEXT,
      secondary: SECONDARY_COLOR_TEXT,
      disabled: SECONDARY_COLOR_TEXT,
    },
    button: {
      primary: {
        main: GRADIENT_BLACK_COLOR,
        contrastText: GRADIENT_BLACK_COLOR_TEXT,
        hover: {
          main: GRADIENT_BLACK_COLOR_HOVER,
          contrastText: GRADIENT_BLACK_COLOR_TEXT,
        },
      },
      secondary: {
        main: "#fff",
        contrastText: "black",
        hover: {
          main: "oklch(.97 0 0)",
          contrastText: "black",
        },
      },
    },
    primary: {
      main: PRIMARY_COLOR,
      contrastText: "#fff",
    },
    secondary: {
      main: SECONDARY_COLOR,
      contrastText: PRIMARY_COLOR_TEXT,
    },
    tertiary: {
      main: "#5cecff",
      contrastText: "#000000",
    },
    white: {
      main: "#ffffff",
      contrastText: "#000000",
    },
    grey: {
      main: GREY_COLOR,
      dark: GREY_DARK_COLOR,
    },
    error: {
      main: RED_COLOR,
      contrastText: "#ffffff",
    },
    disabled: {
      main: DISABLED_COLOR_DARK,
      contrastText: "#000000",
    },
    hover: {
      main: "brightness(1.1)",
    },
    background: {
      paper: PAPER_BACKGROUND_COLOR,
      paperSlideMenu: PAPER_BACKGROUND_COLOR_SLIDE_MENU,
      paperContrastText: PRIMARY_COLOR_TEXT,
      paperSecondary: PAPER_SECONDARY_BACKGROUND_COLOR,
      paperSecondaryContrastText: PRIMARY_COLOR_TEXT,
      default: PAPER_BACKGROUND_COLOR,
    },
    border: {
      grey: `${PAPER_CONTRAST_TEXT}fff25`,
    },
    table: {
      header: {
        background: "#F9FAFB",
        color: PAPER_SECONDARY_CONTRAST_TEXT,
      },
      cell: {
        backgroundOdd: "#fff",
        backgroundEven: "#fff",
        color: PAPER_SECONDARY_CONTRAST_TEXT,
      },
    },
    fields: {
      background: FIELD_BACKGROUND_COLOR,
      placeholder: FIELD_PLACEHOLDER_COLOR_DARK,
    },
    card: {
      background: "#FFF",
    },
  },
};

const initialState = {
  slideMenuIsOpen: false,
  slideMenuIsCollapsed: getSlideMenuCollapsedFromStorage(),
  showModalSelectSubscription: false,
  modalSelectSubscriptionReason: null,
  showOpenChatButton: true,
  notification: {
    open: false,
    vertical: "bottom",
    horizontal: "right",
    message: "",
    action: "",
  },
  showBackdrop: false,
  theme: themeDark,
  version: "",
  lastDateTimeInsights: "",
  agenciesFeatureFlags: {
    importCampaigns: [],
    enterpriseOnlyPlans: [],
    animateImageWithPrompt: [],
    mcp: [],
  },
  npsForm: {
    shouldShow: false,
    actionType: null,
  },
  openModals: {
    isModalCreateWithIA: false,
    isModalGallery: false,
    isPanelStorageFull: false,
    isModalPurchaseCredits: false,
    isModalConfigureBrandLimits: false,
    isModalAdleCardWaitlist: false,
  },
  adleCardWaitlist: {
    isStatusResolved: false,
    hasJoined: false,
    hasDismissed: false,
    openSource: null,
  },
};

export const uiReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.UI_SET_THEME: {
      return {
        ...state,
        theme: {
          ...state.theme,
          palette: {
            ...state.theme.palette,
            button: {
              primary: {
                main:
                  action.payload.primaryButtonColor ||
                  action.payload.primaryColor ||
                  state.theme.palette.button.primary.main,
                contrastText:
                  action.payload.textPrimaryColor ||
                  state.theme.palette.button.primary.contrastText,
                hover: {
                  main:
                    action.payload.primaryButtonColorHover ||
                    action.payload.primaryColor ||
                    state.theme.palette.button.primary.hover.main,
                  contrastText:
                    action.payload.textPrimaryColor ||
                    state.theme.palette.button.primary.hover.contrastText,
                },
              },
              secondary: {
                main:
                  action.payload.secondaryColor ||
                  state.theme.palette.button.secondary.main,
                contrastText:
                  action.payload.textSecondaryColor ||
                  state.theme.palette.button.secondary.contrastText,
                hover: {
                  main:
                    action.payload.secondaryColor ||
                    state.theme.palette.button.secondary.hover.main,
                  contrastText:
                    action.payload.textSecondaryColor ||
                    state.theme.palette.button.secondary.hover.contrastText,
                },
              },
            },
            primary: {
              main:
                action.payload.primaryColor || state.theme.palette.primary.main,
              contrastText:
                action.payload.textPrimaryColor ||
                state.theme.palette.primary.contrastText,
            },
            secondary: {
              main:
                action.payload.secondaryColor ||
                state.theme.palette.secondary.main,
              contrastText:
                action.payload.textSecondaryColor ||
                state.theme.palette.secondary.contrastText,
            },
          },
          logo: action.payload.logo || state.theme.logo,
          logoChat: action.payload.logoChat || state.theme.logoChat,
          logoLogin: action.payload.logoLogin || state.theme.logoLogin,
          logoNavbar: action.payload.logoNavbar || state.theme.logoNavbar,
          logoError: action.payload.logoError || state.theme.logoError,
          logoInvoice: action.payload.logoInvoice || state.theme.logoInvoice,
          favicon: action.payload.favicon || state.theme.favicon,
          nameBusiness: action.payload.nameBusiness || state.theme.nameBusiness,
        },
      };
    }
    case types.UI_SET_OPEN_SLIDE_MENU:
      return {
        ...state,
        slideMenuIsOpen: action.payload,
      };
    case types.UI_SET_SLIDE_MENU_COLLAPSED:
      return {
        ...state,
        slideMenuIsCollapsed: action.payload,
      };
    case types.UI_SET_OPEN_NOTIFICATION:
      return {
        ...state,
        notification: {
          open: action.payload.open,
          vertical: action.payload.vertical,
          horizontal: action.payload.horizontal,
          message: action.payload.message,
          action: action.payload.action,
        },
      };
    case types.UI_SET_SHOW_BACKDROP:
      return {
        ...state,
        showBackdrop: action.payload,
      };
    case types.SET_VERSION: {
      return {
        ...state,
        version: action.payload,
      };
    }
    case types.UI_SHOW_MODAL_SELECT_SUBSCRIPTION: {
      return {
        ...state,
        showModalSelectSubscription: action.payload,
        modalSelectSubscriptionReason: action.payload
          ? (action.reason ?? null)
          : null,
      };
    }
    case types.SET_LAST_DATE_TIME_INSIGHTS: {
      return {
        ...state,
        lastDateTimeInsights: action.payload,
      };
    }
    case types.SET_AGENCIES_FEATURE_FLAGS: {
      return {
        ...state,
        agenciesFeatureFlags: {
          ...state.agenciesFeatureFlags,
          ...action.payload,
        },
      };
    }
    case types.RESET_UI_STATE: {
      return {
        ...initialState,
        version: state.version,
        theme: state.theme,
        lastDateTimeInsights: state.lastDateTimeInsights,
        agenciesFeatureFlags: state.agenciesFeatureFlags,
        slideMenuIsCollapsed: state.slideMenuIsCollapsed,
      };
    }
    case types.GLOBAL_RESET_STATE:
      return {
        ...initialState,
        version: state.version,
        lastDateTimeInsights: state.lastDateTimeInsights,
        agenciesFeatureFlags: state.agenciesFeatureFlags,
        theme: state.theme,
        slideMenuIsCollapsed: state.slideMenuIsCollapsed,
      };
    case types.SHOW_NPS_FORM:
      return {
        ...state,
        npsForm: {
          shouldShow: true,
          actionType: action.payload.actionType,
        },
      };
    case types.HIDE_NPS_FORM:
      return {
        ...state,
        npsForm: {
          shouldShow: false,
          actionType: null,
        },
      };
    case types.UI_SET_MODAL_OPEN_STATE:
      return {
        ...state,
        openModals: {
          ...state.openModals,
          [action.payload.modalName]: action.payload.isOpen,
        },
      };
    case types.UI_SET_ADLE_CARD_WAITLIST_STATUS:
      return {
        ...state,
        adleCardWaitlist: {
          ...state.adleCardWaitlist,
          ...action.payload,
        },
      };

    default:
      return state;
  }
};
